import { Breadcrumb, Layout, Table, Button, Card, message, Popconfirm, Space, Select, Row, Col, Input, Form, Tag, Modal, Descriptions, Divider } from 'antd';
import { Content } from 'antd/es/layout/layout';
import Headerdiv from '../../layout/header';
import Footerdiv from '../../layout/footer';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axiosClient from '../../../api/axiosClient';
import { APPBACKGROUND_COLOR } from '../../appsettings';
import { EditOutlined, DeleteOutlined, PlusOutlined, FilterOutlined, ClearOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const Records = () => {
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [filters, setFilters] = useState({ type: 'all', status: 'all', search: '' });

    // View Modal State
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    // Removed viewLoading state as it is no longer needed

    const navigate = useNavigate();
    const USERID = localStorage.getItem('USERID');

    const fetchRecords = async (page = 1, pageSize = 10, currentFilters = filters) => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page,
                pageSize,
                type: currentFilters.type,
                status: currentFilters.status,
                search: currentFilters.search
            });

            const response = await axiosClient.get(`/inventory/record/get_all_records.php?${queryParams.toString()}`);
            const result = response.data;

            if (result.status === 'success') {
                setRecords(result.data);
                setPagination({
                    current: parseInt(result.page) || 1,
                    pageSize: parseInt(result.pageSize) || 10,
                    total: parseInt(result.total) || 0,
                });
            } else {
                message.error(result.message || 'Failed to fetch records.');
            }
        } catch (error) {
            console.error('Fetch records error:', error);
            message.error(error.response?.data?.message || 'Network error.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRecords(pagination.current, pagination.pageSize, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTableChange = (newPagination) => {
        fetchRecords(newPagination.current, newPagination.pageSize, filters);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const applyFilters = () => {
        fetchRecords(1, pagination.pageSize, filters);
    };

    const clearFilters = () => {
        const resetFilters = { type: 'All', status: 'All' };
        setFilters(resetFilters);
        fetchRecords(1, pagination.pageSize, resetFilters);
    };

    const handleDelete = async (id) => {
        try {
            const response = await axiosClient.post('/inventory/record/delete_record.php', { id, by: USERID });
            if (response.data.status === 'success') {
                message.success('Record deleted successfully');
                fetchRecords();
            } else {
                message.error(response.data.message || 'Failed to delete record');
            }
        } catch (error) {
            console.error('Delete error:', error);
            message.error('Failed to delete record');
        }
    };

    // UPDATED: Now accepts the whole record object directly from the table
    const handleView = (record) => {
        setSelectedRecord(record);
        setViewModalOpen(true);
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
        },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) => text ? dayjs(text).format('DD MMM YYYY hh:mm A') : '-',
        },
        {
            title: 'Status',
            dataIndex: 'record_status', // Changed from 'status' based on your DB schema
            key: 'record_status',
            render: (status) => (
                <Tag color={status === 'Open' ? 'green' : 'red'}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type='primary'
                        size='small'
                        icon={<EyeOutlined />}
                        // UPDATED: Pass the whole 'record' object
                        onClick={() => handleView(record)}
                    />
                    <Button
                        size='small'
                        style={{
                            backgroundColor: "#FFC107",
                            borderColor: "#FFC107",
                            color: "white",
                        }}
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/inventory/records/edit/${record.id}`)}
                    />
                    <Popconfirm
                        title="Are you sure to delete this record?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            size='small'
                            type="primary" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: '100vh' }}>
            <Headerdiv />
            <Content style={{ padding: "10px" }}>
                <Breadcrumb style={{ marginBottom: 10 }}>
                    <Breadcrumb.Item><Link to={"/inventory/itemList"}>Inventory</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>Records List</Breadcrumb.Item>
                </Breadcrumb>

                <Card title="Filter" style={{ marginBottom: "10px" }} extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/inventory/records/create')}
                    >
                        Add Record
                    </Button>
                }>
                    <Form
                        layout="vertical"
                        onFinish={applyFilters}
                        initialValues={filters}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={8} md={6}>
                                <Form.Item label="Search" name="search">
                                    <Input
                                        placeholder="Search..."
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={8} md={6}>
                                <Form.Item label="Type" name="type">
                                    <Select
                                        onChange={(value) => handleFilterChange('type', value)}
                                    >
                                        <Select.Option value="all">All Types</Select.Option>
                                        <Select.Option value="Breakdown">Breakdown</Select.Option>
                                        <Select.Option value="Repair">Repair</Select.Option>
                                        <Select.Option value="Service">Service</Select.Option>
                                        <Select.Option value="Other">Other</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={8} md={6}>
                                <Form.Item label="Status" name="status">
                                    <Select
                                        onChange={(value) => handleFilterChange('status', value)}
                                    >
                                        <Select.Option value="all">All Statuses</Select.Option>
                                        <Select.Option value="Open">Open</Select.Option>
                                        <Select.Option value="Closed">Closed</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={8} md={6}>
                                <Form.Item label=" ">
                                    <Space>
                                        <Button
                                            type="primary"
                                            icon={<FilterOutlined />}
                                            htmlType="submit"
                                        >
                                            Filter
                                        </Button>

                                        <Button
                                            icon={<ClearOutlined />}
                                            onClick={clearFilters}
                                        >
                                            Clear
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>

                <Card title="Inventory Records">
                    <Table
                        columns={columns}
                        dataSource={records}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: true }}
                    />
                </Card>

                <Modal
                    title="Record Details"
                    open={viewModalOpen}
                    onCancel={() => setViewModalOpen(false)}
                    footer={[
                        <Button key="close" onClick={() => setViewModalOpen(false)}>
                            Close
                        </Button>
                    ]}
                    width={800}
                >
                    {selectedRecord ? (
                        <div>
                            <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
                                <Descriptions.Item label="Record ID">{selectedRecord.id}</Descriptions.Item>
                                <Descriptions.Item label="Date">{selectedRecord.date ? dayjs(selectedRecord.date).format('DD MMM YYYY hh:mm A') : '-'}</Descriptions.Item>
                                <Descriptions.Item label="Item Code">{selectedRecord.item_code}</Descriptions.Item>
                                <Descriptions.Item label="Item Name">{selectedRecord.item_name}</Descriptions.Item>
                                <Descriptions.Item label="Type">{selectedRecord.type}</Descriptions.Item>
                                <Descriptions.Item label="Note">{selectedRecord.note}</Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={selectedRecord.record_status === 'Open' ? 'green' : 'red'}>
                                        {selectedRecord.record_status}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Created By">{selectedRecord.created_by_name || selectedRecord.created_by || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Created At">{selectedRecord.created_at ? dayjs(selectedRecord.created_at).format('DD MMM YYYY hh:mm A') : '-'}</Descriptions.Item>
                            </Descriptions>

                            <Divider orientation="left">Description</Divider>
                            {selectedRecord.description ? (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: selectedRecord.description
                                    }}
                                    style={{
                                        padding: '10px',
                                        border: '1px solid #f0f0f0',
                                        borderRadius: '4px',
                                        backgroundColor: '#fafafa'
                                    }}
                                />
                            ) : (
                                <p style={{ color: '#999' }}>No description provided.</p>
                            )}
                        </div>
                    ) : (
                        <p>No record details available.</p>
                    )}
                </Modal>
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default Records;